/**
 * The apply link.
 *
 * Applications are never taken on this site: the button records the outbound
 * click, which is the receipt the company sees in its dashboard, and then sends
 * the seeker to the company's own posting. It stays a real anchor so it can be
 * opened in a new tab, and a failure to record the click never stops a seeker
 * reaching the role.
 */

import type { MouseEvent, ReactNode } from 'react';

import { useApplyClick } from '../lib/queries';
import { ExternalArrow } from './ui';

export function ApplyButton({
  jobId,
  applyUrl,
  companyName,
  label,
  compact = false,
}: {
  readonly jobId: string;
  readonly applyUrl: string;
  readonly companyName: string;
  readonly label?: string;
  readonly compact?: boolean;
}): ReactNode {
  const record = useApplyClick();

  function handleClick(event: MouseEvent<HTMLAnchorElement>): void {
    // Let modified clicks and middle clicks behave exactly as the browser
    // intends; the count is not worth breaking that.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
    // Fire and forget. The anchor's own target carries the seeker to the
    // posting in a new tab, so recording the click never stands between them
    // and applying, and this tab stays alive to finish the request. Cancelling
    // the default and navigating here instead would make target="_blank" a
    // promise the element does not keep: the listing, and the board behind it,
    // would be replaced by the employer's site.
    record.mutate(jobId);
  }

  return (
    <a
      className={compact ? 'cta is-compact' : 'cta'}
      href={applyUrl}
      rel="nofollow noopener external"
      target="_blank"
      onClick={handleClick}
    >
      {label ?? `Apply on ${companyName}’s site`}
      <ExternalArrow />
    </a>
  );
}
